import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { SOCKET_URL } from '../config/env';

// Types
export interface ChatParticipant {
  userId: {
    _id: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    email: string;
  };
  role: 'buyer' | 'seller' | 'admin';
  joinedAt: string;
  isActive: boolean;
  unreadCount: number;
}

export interface ChatMessage {
  _id: string;
  content: string;
  senderId: string;
  senderName: string;
  senderRole: 'buyer' | 'seller' | 'admin';
  messageType: 'text' | 'image' | 'file';
  sentAt: string;
  isRead: boolean;
}

export interface DisputeChat {
  _id: string;
  type: string;
  status: string;
  title: string;
  description: string;
  participants: ChatParticipant[];
  orderId?: {
    _id: string;
    orderNumber: string;
  };
  orderItemId?: {
    _id: string;
    productTitle: string;
  };
  totalMessages: number;
  lastMessage?: {
    content: string;
    senderId: string;
    sentAt: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface ChatState {
  socket: Socket | null;
  isConnected: boolean;
  chats: DisputeChat[];
  activeChat: DisputeChat | null;
  messages: Record<string, ChatMessage[]>;
  loading: boolean;
  error: string | null;
}

interface ChatContextType {
  state: ChatState;
  connectSocket: () => void;
  disconnectSocket: () => void;
  sendMessage: (chatId: string, content: string) => Promise<void>;
  joinChat: (chatId: string) => void;
  leaveChat: (chatId: string) => void;
  setActiveChat: (chat: DisputeChat | null) => void;
  refreshChats: () => void;
  setMessages: (chatId: string, messages: ChatMessage[]) => void;
}

type ChatAction =
  | { type: 'SET_SOCKET'; payload: Socket | null }
  | { type: 'SET_CONNECTED'; payload: boolean }
  | { type: 'SET_CHATS'; payload: DisputeChat[] }
  | { type: 'SET_ACTIVE_CHAT'; payload: DisputeChat | null }
  | { type: 'SET_MESSAGES'; payload: { chatId: string; messages: ChatMessage[] } }
  | { type: 'ADD_MESSAGE'; payload: { chatId: string; message: ChatMessage } }
  | { type: 'UPDATE_CHAT'; payload: DisputeChat }
  | {
      type: 'UPDATE_CHAT_LAST_MESSAGE';
      payload: { chatId: string; lastMessage: { content: string; senderId: string; sentAt: string } };
    }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null };

const initialState: ChatState = {
  socket: null,
  isConnected: false,
  chats: [],
  activeChat: null,
  messages: {},
  loading: false,
  error: null,
};

const chatReducer = (state: ChatState, action: ChatAction): ChatState => {
  switch (action.type) {
    case 'SET_SOCKET':
      return { ...state, socket: action.payload };
    case 'SET_CONNECTED':
      return { ...state, isConnected: action.payload };
    case 'SET_CHATS':
      return { ...state, chats: action.payload };
    case 'SET_ACTIVE_CHAT':
      return { ...state, activeChat: action.payload };
    case 'SET_MESSAGES':
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.chatId]: action.payload.messages,
        },
      };
    case 'ADD_MESSAGE':
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.chatId]: [...(state.messages[action.payload.chatId] || []), action.payload.message],
        },
      };
    case 'UPDATE_CHAT':
      if (!action.payload || !action.payload._id) {
        console.warn('UPDATE_CHAT called with invalid payload:', action.payload);
        return state;
      }
      if (!Array.isArray(state.chats)) {
        console.warn('state.chats is not an array:', state.chats);
        return { ...state, chats: [] };
      }
      return {
        ...state,
        chats: state.chats.map((chat) => {
          if (!chat || !chat._id) {
            console.warn('Invalid chat in UPDATE_CHAT:', chat);
            return chat;
          }
          return chat._id === action.payload._id ? action.payload : chat;
        }),
      };
    case 'UPDATE_CHAT_LAST_MESSAGE':
      if (!action.payload || !action.payload.chatId) {
        console.warn('UPDATE_CHAT_LAST_MESSAGE called with invalid payload:', action.payload);
        return state;
      }
      if (!Array.isArray(state.chats)) {
        console.warn('state.chats is not an array in UPDATE_CHAT_LAST_MESSAGE:', state.chats);
        return { ...state, chats: [] };
      }
      return {
        ...state,
        chats: state.chats.map((chat) => {
          if (!chat || !chat._id) {
            console.warn('Invalid chat in chats array:', chat);
            return chat;
          }
          return chat._id === action.payload.chatId
            ? {
                ...chat,
                lastMessage: action.payload.lastMessage,
                totalMessages: (chat.totalMessages || 0) + 1,
              }
            : chat;
        }),
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    default:
      return state;
  }
};

const ChatContext = createContext<ChatContextType | undefined>(undefined);

let socketInstance: Socket | null = null;

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);

  const connectSocket = useCallback(() => {
    const token = localStorage.getItem('accessToken');

    if (!token) {
      dispatch({ type: 'SET_ERROR', payload: 'Authentication token not found' });
      return;
    }

    if (socketInstance) {
      dispatch({ type: 'SET_SOCKET', payload: socketInstance });
      return;
    }

    // Connect to Socket.IO server
    const socketUrl = SOCKET_URL;

    socketInstance = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      timeout: 10000,
      autoConnect: true,
    });

    // Connection events
    socketInstance.on('connect', () => {
      console.log('✅ Admin chat socket connected');
      dispatch({ type: 'SET_CONNECTED', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('❌ Admin chat socket disconnected:', reason);
      dispatch({ type: 'SET_CONNECTED', payload: false });
    });

    socketInstance.on('connect_error', (error) => {
      console.error('🚨 Socket connection error:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Connection error: ' + error.message });
    });

    // Chat events
    socketInstance.on('chat_created', (data: { chat: DisputeChat }) => {
      console.log('🆕 New chat created:', data);
      if (data && data.chat && data.chat._id) {
        dispatch({ type: 'UPDATE_CHAT', payload: data.chat });
      } else {
        console.warn('Invalid chat_created data:', data);
      }
    });

    socketInstance.on('new_message', (data: any) => {
      console.log('📨 New message received:', data);

      // Handle both formats:
      // 1. {message, chatId} from chat room
      // 2. {message, chatUpdate} from user room
      const message = data.message || data;
      const chatId = data.chatId || (data.chatUpdate && data.chatUpdate._id);

      if (message && message._id && chatId) {
        dispatch({ type: 'ADD_MESSAGE', payload: { chatId, message } });

        // Update chat's last message - use chatUpdate if available, otherwise create from message
        const lastMessage = data.chatUpdate?.lastMessage || {
          content: message.content || '',
          senderId: message.senderId || '',
          sentAt: message.sentAt || new Date().toISOString(),
        };

        dispatch({
          type: 'UPDATE_CHAT_LAST_MESSAGE',
          payload: {
            chatId,
            lastMessage,
          },
        });
      } else {
        console.warn('Invalid new_message data:', data);
      }
    });

    socketInstance.on('message_sent', (data: { message: ChatMessage; chatId: string }) => {
      console.log('✅ Message sent confirmation:', data);
      dispatch({ type: 'ADD_MESSAGE', payload: { chatId: data.chatId, message: data.message } });
    });

    socketInstance.on('joined_chat', (data: { chatId: string }) => {
      console.log('🏠 Joined chat room:', data.chatId);
    });

    socketInstance.on('left_chat', (data: { chatId: string }) => {
      console.log('👋 Left chat room:', data.chatId);
    });

    socketInstance.on('user_typing', (data: { chatId: string; userId: string; isTyping: boolean }) => {
      console.log('⌨️ User typing:', data);
      // Handle typing indicator
    });

    dispatch({ type: 'SET_SOCKET', payload: socketInstance });
  }, [state.chats]);

  const disconnectSocket = useCallback(() => {
    if (socketInstance) {
      socketInstance.disconnect();
      socketInstance = null;
    }
    dispatch({ type: 'SET_SOCKET', payload: null });
    dispatch({ type: 'SET_CONNECTED', payload: false });
  }, []);

  const sendMessage = useCallback(
    async (chatId: string, content: string): Promise<void> => {
      if (!socketInstance || !state.isConnected) {
        throw new Error('Socket not connected');
      }

      // Get current admin info for optimistic update
      const getCurrentAdmin = () => {
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          return {
            id: user.id || user._id || 'admin',
            name: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name || 'ادمین',
          };
        } catch {
          return { id: 'admin', name: 'ادمین' };
        }
      };

      const admin = getCurrentAdmin();

      // Create optimistic message for immediate display
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const optimisticMessage: ChatMessage = {
        _id: tempId,
        content,
        senderId: admin.id,
        senderName: admin.name,
        senderRole: 'admin',
        messageType: 'text',
        sentAt: new Date().toISOString(),
        isRead: false,
      };

      // Add optimistic message immediately
      dispatch({ type: 'ADD_MESSAGE', payload: { chatId, message: optimisticMessage } });

      return new Promise((resolve, reject) => {
        socketInstance!.emit(
          'send_message',
          {
            chatId,
            content,
            messageType: 'text',
          },
          (response: { success: boolean; message?: ChatMessage; error?: string }) => {
            if (response.success && response.message) {
              // Replace optimistic message with real message
              // Note: The reducer doesn't have REPLACE, so we'll just add it (duplicates filtered by component)
              resolve();
            } else if (response.success) {
              resolve();
            } else {
              // On error, we might want to remove the optimistic message
              reject(new Error(response.error || 'Failed to send message'));
            }
          }
        );
      });
    },
    [state.isConnected]
  );

  const joinChat = useCallback((chatId: string) => {
    if (socketInstance) {
      socketInstance.emit('join_chat', { chatId });
    }
  }, []);

  const leaveChat = useCallback((chatId: string) => {
    if (socketInstance) {
      socketInstance.emit('leave_chat', { chatId });
    }
  }, []);

  const setActiveChat = useCallback(
    (chat: DisputeChat | null) => {
      if (state.activeChat) {
        leaveChat(state.activeChat._id);
      }

      dispatch({ type: 'SET_ACTIVE_CHAT', payload: chat });

      if (chat) {
        joinChat(chat._id);
      }
    },
    [state.activeChat, joinChat, leaveChat]
  );

  const refreshChats = useCallback(() => {
    // This will be called by the parent component to refresh chats
    dispatch({ type: 'SET_LOADING', payload: true });
  }, []);

  const setMessages = useCallback((chatId: string, messages: ChatMessage[]) => {
    dispatch({
      type: 'SET_MESSAGES',
      payload: { chatId, messages },
    });
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    connectSocket();

    return () => {
      disconnectSocket();
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (socketInstance) {
        socketInstance.disconnect();
        socketInstance = null;
      }
    };
  }, []);

  const value: ChatContextType = {
    state,
    connectSocket,
    disconnectSocket,
    sendMessage,
    joinChat,
    leaveChat,
    setActiveChat,
    refreshChats,
    setMessages,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};

export default ChatContext;
